package com.evoapps.evoshift.domain.usecase.owner

import com.evoapps.evoshift.domain.model.GroupManager
import com.evoapps.evoshift.domain.model.GroupManagers
import com.evoapps.evoshift.domain.model.InvalidGroupManagersException
import com.evoapps.evoshift.domain.model.InvalidWorkplaceGroupsException
import com.evoapps.evoshift.domain.model.WorkplaceGroup
import com.evoapps.evoshift.domain.model.WorkplaceGroups
import com.evoapps.evoshift.domain.repository.WorkplaceGroupRepository

/** SH-267: the owner's workplace groups, by name (the default group first). */
class GetWorkplaceGroupsUseCase(private val repository: WorkplaceGroupRepository) {
    suspend operator fun invoke(ownerId: String): Result<List<WorkplaceGroup>> = repository.getGroups(ownerId).map { groups ->
        groups.sortedWith(compareByDescending<WorkplaceGroup> { it.isDefault }.thenBy { it.name.lowercase() })
    }
}

/**
 * SH-267: saves the owner's grouping of [owned] workplaces after the same checks the server makes
 * (names, each workplace in one group, only their own), so the screen can say what is wrong
 * before a round trip.
 */
class SaveWorkplaceGroupsUseCase(private val repository: WorkplaceGroupRepository) {
    suspend operator fun invoke(groups: List<WorkplaceGroup>, owned: Set<String>): Result<List<WorkplaceGroup>> = runCatching {
        val trimmed = groups.map { it.copy(name = it.name.trim()) }
        val problems = WorkplaceGroups.problems(trimmed, owned)
        if (problems.isNotEmpty()) throw InvalidWorkplaceGroupsException(problems)
        repository.saveGroups(trimmed).getOrThrow()
    }
}

/**
 * SH-268: saves the managers of [group] (its whole list) after the checks the server makes too
 * (levels, each person once, not the owner), so the screen can say what is wrong at once.
 */
class SaveGroupManagersUseCase(private val repository: WorkplaceGroupRepository) {
    suspend operator fun invoke(group: WorkplaceGroup, managers: List<GroupManager>): Result<List<GroupManager>> = runCatching {
        GroupManagers.problem(managers, group.ownerId)?.let { throw InvalidGroupManagersException(it) }
        repository.saveManagers(group.id, managers).getOrThrow()
    }
}
